// Shared logic for seeding a page's project variables into the page-vars
// store, so blocks / bindings / embed args / filters / `{{tokens}}` can use
// them directly — no `api` input needed. Every render surface (in-app preview
// + smart docs, downloaded runtime/client, published site) calls this with the
// variables it fetched from its own backend, so the behaviour is identical:
//
//   vars[name]           → the resolved VALUE
//                            • prompt : the user's selected value (or default)
//                            • value  : the variable's static value
//   vars[name + '__label'] → the human-readable LABEL
//                            • prompt/select : the matching option's col2
//                            • otherwise      : the value itself
//
// A page-input `default` written as the token ":name" is resolved to that
// variable's value too.

import type { PromptVariable } from './PromptModal'

/** A static (`value`-kind) project variable: just a name + stored value. */
export interface ValueVariable {
  name: string
  value: string
}

/** A page input we may resolve a ":name" default for. */
export interface SeedInput {
  name: string
  default?: string
}

export interface ProjectVarSeedArgs {
  /** Prompt (per-user) variables — value + option labels. */
  promptVars?: PromptVariable[]
  /** Static (`value`-kind) project variables. */
  valueVars?: ValueVariable[]
  /** Page inputs, for resolving ":name" defaults to a variable's value. */
  inputs?: SeedInput[]
}

/**
 * Build the `{ varName: value }` patch to merge into the page-vars store.
 * Returns an empty object when nothing resolves. Pure + side-effect free.
 */
export function buildProjectVarSeed(args: ProjectVarSeedArgs): Record<string, unknown> {
  const { promptVars = [], valueVars = [], inputs = [] } = args
  // name → { value, label } for every variable we can resolve.
  const resolved: Record<string, { value: unknown; label: unknown }> = {}

  // Static (`value`) variables — use the stored value as-is.
  for (const v of valueVars) {
    if (!v?.name) continue
    if (v.value == null || v.value === '') continue
    resolved[v.name] = { value: v.value, label: v.value }
  }
  // Prompt variables — the user's value (or default) + the option label.
  for (const pv of promptVars) {
    if (!pv?.name) continue
    const val = pv.value != null && pv.value !== '' ? pv.value : pv.default
    if (val == null || val === '') continue
    const opt = (pv.options || []).find((o) => String(o.value) === String(val))
    resolved[pv.name] = { value: val, label: opt ? opt.label : val }
  }

  const out: Record<string, unknown> = {}
  for (const [name, r] of Object.entries(resolved)) {
    out[name] = r.value
    out[`${name}__label`] = r.label
  }
  // Resolve `:name` input defaults to the variable's value.
  for (const inp of inputs) {
    const d = inp.default
    if (typeof d === 'string' && d.startsWith(':')) {
      const key = d.slice(1)
      if (key in resolved) out[inp.name] = resolved[key].value
    }
  }
  return out
}
